import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';
import useAppStore from '../store/useAppStore';
import { levels } from '../data/levels';
import { stages } from '../data/stages';
import { Stage, Module } from '../types';
import styles from './StageComplete.module.css';

const calcStars = (correct: number, total: number): number => {
  if (total === 0) return 0;
  const rate = correct / total;
  if (rate >= 0.9) return 3;
  if (rate >= 0.7) return 2;
  if (rate >= 0.5) return 1;
  return 0;
};

/** 4个阶段对应的成长叙事 */
const GROWTH_NARRATIVES: Record<Stage, string[]> = {
  [Stage.NEWBORN]: [
    '从产房到第一个家，你陪伴宝宝走过了生命中最脆弱也最宝贵的 28 天。',
    '那些半夜爬起来的喂养、小心翼翼的洗澡、对每一片尿布的关注，都在帮宝宝建立对这个世界最初的信任。',
    '你可能还不知道——宝宝已经认识你的声音、嗅出你的气味，他比以前更依赖你。',
    '欢迎来到育儿世界。这段旅程才刚刚开始，你已经迈出了最重要的一步。',
  ],
  [Stage.ONE_TO_THREE]: [
    '你熬过了最初的手忙脚乱，现在宝宝开始"回应"你了：第一个社交性微笑、第一声咕咕细语。',
    '宝宝可能在某天深夜忽然翻过身，也可能在某次猛长期里"挂"在你怀里不停吃奶——这些都是成长的信号。',
    '满月后的日常开始有了节奏：喂奶间隔拉长、夜间睡眠变长、白天多了许多清醒互动的时光。',
    '你们一起走过了最需要"磨合"的头 3 个月。后面的日子，宝宝将开始更主动地探索世界。',
  ],
  [Stage.FOUR_TO_SIX]: [
    '从纯奶到第一口辅食，从只能躺着到会翻身、会坐——这 3 个月的变化堪称一场迷你革命。',
    '宝宝不再只是一个"需要被照顾"的小生命，他开始向你伸手、对你大笑、用全部感官去认识周围的一切。',
    '你学会了在呛奶时冷静应对、在辅食里谨慎试验、在翻身期加固家中每一个角落。',
    '4-6 个月的宝宝，正在从一个新生儿"蜕皮"成一个真正的小小人。你见证并守护了这场蜕变。',
  ],
  [Stage.SEVEN_TO_TWELVE]: [
    '这是第一年里变化最大的半年：宝宝从爬到走、从咿呀到说出第一个词、从只会吃到自己抓取食物。',
    '你经历了分离焦虑的高峰、见证了钳状抓握的诞生、陪伴他在婴儿床里练习了无数次扶站和摔倒。',
    '这时候的宝宝已经有了鲜明的个性：爱笑还是谨慎、外向还是观察者——而你比任何人都更了解他。',
    '恭喜你，陪宝宝走完了生命第一年的学习旅程。这 12 个月种下的理解与知识，将守护你们走更远的路。',
  ],
};

/** 阶段对应的emoji图标 */
const STAGE_ICONS: Record<Stage, string> = {
  [Stage.NEWBORN]: '🏡',
  [Stage.ONE_TO_THREE]: '🌿',
  [Stage.FOUR_TO_SIX]: '🌸',
  [Stage.SEVEN_TO_TWELVE]: '🏃',
};

const MODULE_EMOJIS: Record<Module, string> = {
  [Module.FEEDING]: '🍼',
  [Module.SLEEP]: '🌙',
  [Module.DAILY_CARE]: '🛁',
  [Module.SAFETY]: '🛡️',
  [Module.DEVELOPMENT]: '📈',
};

const StageComplete: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stage } = (location.state as { stage: Stage } | null) || {};

  const completedLevels = useAppStore((s) => s.completedLevels);
  const totalStars = useAppStore((s) => s.getTotalStars());
  const currentTitle = useAppStore((s) => s.getCurrentTitle());
  // 如果没有通过路由传 stage，尝试从 badgeAuxData 推断
  const resolvedStage = stage || (() => {
    const aux = useAppStore.getState().badgeAuxData;
    if (aux.stageCompleted.length > 0) {
      return aux.stageCompleted[aux.stageCompleted.length - 1] as Stage;
    }
    return null;
  })();

  // 该阶段所有关卡
  const stageLevels = useMemo(() => {
    if (!resolvedStage) return [];
    return levels.filter((l) => l.stage === resolvedStage);
  }, [resolvedStage]);

  // 按模块统计
  const moduleSummary = useMemo(() => {
    if (!resolvedStage) return [];
    const modules = stages.find((s) => s.name === resolvedStage)?.modules || [];
    return modules.map((mod) => {
      const modLevels = stageLevels.filter((l) => l.module === mod);
      const modCompleted = modLevels.map((l) => {
        const records = completedLevels.filter(
          (cl) => cl.levelId === l.id
        );
        if (records.length === 0) return { levelId: l.id, title: l.title, correct: 0, total: 0, stars: 0 };
        const latest = records.reduce((a, b) => (a.timestamp > b.timestamp ? a : b));
        return {
          levelId: l.id,
          title: l.title,
          correct: latest.correctCount,
          total: latest.totalCount,
          stars: calcStars(latest.correctCount, latest.totalCount),
        };
      });
      const totalCorrect = modCompleted.reduce((s, l) => s + l.correct, 0);
      const totalQ = modCompleted.reduce((s, l) => s + l.total, 0);
      const avgAccuracy = totalQ > 0 ? Math.round((totalCorrect / totalQ) * 100) : 0;
      const totalStarsForMod = modCompleted.reduce((s, l) => s + l.stars, 0);
      return {
        module: mod,
        completed: modCompleted.length,
        total: modLevels.length,
        avgAccuracy,
        totalStars: totalStarsForMod,
        details: modCompleted,
      };
    });
  }, [resolvedStage, stageLevels, completedLevels]);

  // 阶段总星数 / 平均正确率
  const stageStats = useMemo(() => {
    const allCorrect = moduleSummary.reduce((s, m) => {
      const correct = m.details.reduce((sum, d) => sum + d.correct, 0);
      return s + correct;
    }, 0);
    const allTotal = moduleSummary.reduce((s, m) => {
      const total = m.details.reduce((sum, d) => sum + d.total, 0);
      return s + total;
    }, 0);
    const stageStars = moduleSummary.reduce((s, m) => s + m.totalStars, 0);
    return {
      totalCorrect: allCorrect,
      totalQ: allTotal,
      accuracy: allTotal > 0 ? Math.round((allCorrect / allTotal) * 100) : 0,
      stars: stageStars,
      maxStars: stageLevels.length * 3,
    };
  }, [moduleSummary, stageLevels.length]);

  const narrative = resolvedStage ? GROWTH_NARRATIVES[resolvedStage] : [];

  // 当前阶段有多少关已达1星
  const currentStageLevelsWithStar = useMemo(() => {
    return moduleSummary.reduce(
      (count, m) => count + m.details.filter((d) => d.stars >= 1).length, 0
    );
  }, [moduleSummary]);

  // 找到下一阶段
  const nextStage = useMemo(() => {
    if (!resolvedStage) return null;
    const idx = stages.findIndex((s) => s.name === resolvedStage);
    if (idx >= 0 && idx < stages.length - 1) {
      return stages[idx + 1];
    }
    return null;
  }, [resolvedStage]);

  // 当前阶段是否满足质量门槛（每关至少1星）
  const currentStageQualityMet = useMemo(() => {
    if (!resolvedStage) return false;
    const levelBest: Record<string, { correctCount: number; totalCount: number; timestamp: number }> = {};
    completedLevels.forEach((cl) => {
      if (!levelBest[cl.levelId] || cl.timestamp > levelBest[cl.levelId].timestamp) {
        levelBest[cl.levelId] = { correctCount: cl.correctCount, totalCount: cl.totalCount, timestamp: cl.timestamp };
      }
    });
    return stageLevels.every((l) => {
      const best = levelBest[l.id];
      return best && best.totalCount > 0 && best.correctCount / best.totalCount >= 0.5;
    });
  }, [resolvedStage, stageLevels, completedLevels]);

  // 下一阶段是否已解锁（月龄保底 或 当前阶段质量自动达标）
  const nextStageAlreadyUnlocked = useMemo(() => {
    if (!nextStage) return false;
    const babyStage = useAppStore.getState().currentStage;
    const babyStageIdx = stages.findIndex((s) => s.name === babyStage);
    const nextIdx = stages.findIndex((s) => s.name === nextStage.name);
    // 月龄保底：宝宝已到达下一阶段
    if (babyStageIdx >= 0 && nextIdx <= babyStageIdx) return true;
    // 质量自动解锁：当前阶段所有关卡每关至少1星
    return currentStageQualityMet;
  }, [nextStage, currentStageQualityMet]);

  // 强化版撒花
  useEffect(() => {
    if (!resolvedStage) return;
    const duration = 3500;
    const end = Date.now() + duration;
    const colors = ['#7bc67e', '#f0c060', '#e8876b', '#c96a3a', '#a9d6e5', '#d4a5d9'];

    const frame = () => {
      confetti({
        particleCount: 6,
        angle: 60 + Math.random() * 60,
        spread: 80,
        origin: { x: Math.random(), y: 0.2 + Math.random() * 0.3 },
        colors,
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    // 开始时的爆发
    confetti({
      particleCount: 120,
      spread: 100,
      origin: { y: 0.5 },
      colors,
      gravity: 0.8,
      scalar: 1.2,
    });

    frame();
  }, [resolvedStage]);

  // 分享文案
  const shareText = `🎖️ 育儿成长闯关 · 阶段完成！\n\n我在「${resolvedStage || ''}」阶段完成了全部 ${stageLevels.length} 关学习，正确率达到 ${stageStats.accuracy}%！\n\n一起来学科学育儿吧！`;

  const [copied, setCopied] = useState(false);
  const handleCopyShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareText]);

  if (!resolvedStage) {
    return (
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button className={styles.homeLink} onClick={() => navigate(-1)}>
            ← 返回
          </button>
        </div>
        <div className={styles.card}>
          <p>未找到完成的阶段信息，请从关卡结果页进入。</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.homeLink} onClick={() => navigate(-1)}>
          ← 返回
        </button>
      </div>
      <div className={styles.card}>
        {/* 顶部庆典 */}
        <div className={styles.celebration}>{STAGE_ICONS[resolvedStage] || '🎉'}</div>
        <div className={styles.badge}>🏆 阶段完成</div>
        <h1 className={styles.title}>
          恭喜你完成了<br />「{resolvedStage}」
        </h1>
        <p className={styles.subtitle}>
          {currentTitle} · 累计 ⭐ {totalStars} 颗
        </p>

        {/* 统计数据 */}
        <div className={styles.statsRow}>
          <div className={styles.statBox}>
            <span className={styles.statNum}>{stageStats.accuracy}%</span>
            <span className={styles.statLabel}>阶段正确率</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statNum}>{stageStats.stars}/{stageStats.maxStars}</span>
            <span className={styles.statLabel}>阶段星数</span>
          </div>
          <div className={styles.statBox}>
            <span className={styles.statNum}>{stageLevels.length}</span>
            <span className={styles.statLabel}>全部关卡</span>
          </div>
        </div>

        {/* 成长叙事 */}
        {narrative.length > 0 && (
          <div className={styles.narrative}>
            {narrative.map((line, i) => (
              <p key={i} className={styles.narrativeLine}>
                <span className={styles.narrativeEmoji}>
                  {i === 0 ? '🌟' : i === 1 ? '💭' : i === 2 ? '✨' : '💛'}
                </span>
                {line}
              </p>
            ))}
          </div>
        )}

        {/* 模块完成摘要 */}
        <div className={`${styles.moduleSummary} ${styles.fadeUp} ${styles.delay1}`}>
          <h3 className={styles.moduleSummaryTitle}>各模块完成情况</h3>
          {moduleSummary.map((m) => (
            <div
              key={m.module}
              className={`${styles.moduleRow} ${m.completed === m.total ? styles.moduleRowDone : ''}`}
            >
              <span className={styles.moduleName}>
                {MODULE_EMOJIS[m.module]} {m.module}
              </span>
              <span className={styles.moduleStars}>
                {'⭐'.repeat(m.totalStars)}
              </span>
              <span className={styles.moduleProgress}>
                {m.avgAccuracy}% · {m.completed}/{m.total} 关
              </span>
            </div>
          ))}
        </div>

        {/* 前方旅程预览：展示后续阶段的解锁路线图 */}
        {(() => {
          const currentIdx = stages.findIndex((s) => s.name === resolvedStage);
          const futureStages = stages.slice(currentIdx + 1);
          if (futureStages.length === 0) return null;

          // 复用 nextStageUnlocked 的判断逻辑：检查每一关是否达标
          const getStageUnlockStatus = (stageName: Stage): boolean => {
            const babyIdx = stages.findIndex((s) => s.name === useAppStore.getState().currentStage);
            const targetIdx = stages.findIndex((s) => s.name === stageName);
            if (babyIdx >= 0 && targetIdx <= babyIdx) return true; // 月龄保底

            // 检查前一阶段每关是否至少1星
            const prevStageName = stages[targetIdx - 1]?.name;
            if (!prevStageName) return false;
            const prevLevels = levels.filter((l) => l.stage === prevStageName);
            if (prevLevels.length === 0) return false;
            const levelBest: Record<string, { correctCount: number; totalCount: number; timestamp: number }> = {};
            completedLevels.forEach((cl) => {
              if (!levelBest[cl.levelId] || cl.timestamp > levelBest[cl.levelId].timestamp) {
                levelBest[cl.levelId] = { correctCount: cl.correctCount, totalCount: cl.totalCount, timestamp: cl.timestamp };
              }
            });
            return prevLevels.every((l) => {
              const best = levelBest[l.id];
              return best && best.totalCount > 0 && best.correctCount / best.totalCount >= 0.5;
            });
          };

          return (
            <div className={`${styles.roadmap} ${styles.fadeUp} ${styles.delay1}`}>
              <h3 className={styles.roadmapTitle}>🔮 前方旅程预览</h3>
              <div className={styles.roadmapList}>
                {/* 当前阶段 - 已完成 */}
                <div className={`${styles.roadmapItem} ${styles.roadmapItemDone}`}>
                  <span className={styles.roadmapIcon}>{STAGE_ICONS[resolvedStage] || '✅'}</span>
                  <div className={styles.roadmapInfo}>
                    <span className={styles.roadmapStageName}>{resolvedStage}</span>
                    <span className={styles.roadmapStatus}>✅ 已完成</span>
                  </div>
                </div>
                {/* 后续阶段 */}
                {futureStages.map((fs, idx) => {
                  const unlocked = getStageUnlockStatus(fs.name);
                  const prevName = stages[stages.findIndex((s) => s.name === fs.name) - 1]?.name;
                  return (
                    <div
                      key={fs.id}
                      className={`${styles.roadmapItem} ${unlocked ? styles.roadmapItemUnlocked : styles.roadmapItemLocked}`}
                    >
                      <span className={styles.roadmapConnector} />
                      <span className={styles.roadmapIcon}>
                        {unlocked ? (STAGE_ICONS[fs.name] || '🔓') : '🔒'}
                      </span>
                      <div className={styles.roadmapInfo}>
                        <span className={styles.roadmapStageName}>{fs.name}</span>
                        <span className={styles.roadmapStatus}>
                          {unlocked
                            ? '🔓 已解锁'
                            : `完成「${prevName || ''}」全部关卡，每关至少1星后解锁`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* 行动按钮 */}
        <div className={`${styles.actions} ${styles.fadeUp} ${styles.delay2}`}>
          {nextStage ? (
            nextStageAlreadyUnlocked ? (
              <button
                className={styles.primaryBtn}
                onClick={() => navigate('/map')}
              >
                前往「{nextStage.name}」继续学习 →
              </button>
            ) : (
              <>
                <div className={styles.lockHint}>
                  <span className={styles.lockHintIcon}>🔒</span>
                  <div>
                    <p className={styles.lockHintTitle}>「{nextStage.name}」尚未解锁</p>
                    <p className={styles.lockHintDesc}>
                      当前阶段 <strong>{stageLevels.length}</strong> 关需<strong>每关至少获得 1 星</strong>（正确率 ≥ 50%）才能解锁下一阶段，目前 <strong>{currentStageLevelsWithStar}/{stageLevels.length}</strong> 关达标。
                    </p>
                    <p className={styles.lockHintAction}>
                      请重新学习得分不足的关卡（🌟 以下），达标后即可解锁下一阶段。
                    </p>
                  </div>
                </div>
                <button
                  className={styles.primaryBtn}
                  onClick={() => navigate('/map')}
                >
                  查看学习地图，提升星数 →
                </button>
              </>
            )
          ) : (
            <button
              className={styles.primaryBtn}
              onClick={() => navigate('/map')}
            >
              查看学习地图 →
            </button>
          )}
          <button
            className={styles.secondaryBtn}
            onClick={() => navigate('/review')}
          >
            📚 复习薄弱知识点
          </button>
          <button
            className={`${styles.shareBtn} ${copied ? styles.copied : ''}`}
            onClick={handleCopyShare}
          >
            {copied ? '✅ 已复制' : '📋 分享这份成长的喜悦'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StageComplete;
